import React, { useEffect, useState } from "react";
import axios from "axios";
import { FileSearch, ChevronDown, ChevronRight } from "lucide-react";
import { API } from "../../auth/AuthContext";
import { pageItems } from "../../lib/pageItems";
import { toast } from "sonner";

export default function AdminKycLogsPanel() {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/kyc/logs?limit=50${filter ? `&status=${filter}` : ""}`, { withCredentials: true });
      setLogs(pageItems(r.data));
    } catch (e) { toast.error("Couldn't load KYC logs"); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter]);

  const colorFor = (s) => s === "approved" ? "var(--zc-success)" : s === "declined" ? "var(--zc-danger,#dc2626)" : s === "manual_review" ? "var(--zc-warn,#f59e0b)" : "var(--zc-text-3)";

  return (
    <div className="zc-card overflow-hidden mt-4" data-testid="admin-kyc-logs">
      <div className="px-4 py-3 border-b border-[var(--zc-border)] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileSearch size={14} className="text-[var(--zc-text-3)]" />
          <div className="eyebrow">Recent KYC attempts (last 50)</div>
        </div>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="zc-input h-8 px-2 text-xs" data-testid="kyc-logs-filter">
            <option value="">All statuses</option>
            <option value="approved">Approved</option>
            <option value="manual_review">Manual review</option>
            <option value="declined">Declined</option>
            <option value="rejected">Rejected</option>
          </select>
          <button onClick={refresh} className="px-3 h-8 zc-btn-ghost text-xs" data-testid="kyc-logs-refresh">Refresh</button>
        </div>
      </div>
      {loading && <div className="px-6 py-6 text-center text-[var(--zc-text-3)] text-xs">Loading…</div>}
      {!loading && logs.length === 0 && <div className="px-6 py-10 text-center text-[var(--zc-text-3)] text-sm">No attempts yet.</div>}
      {logs.map((l, i) => {
        const isOpen = !!expanded[l.kyc_id];
        const score = (typeof l.liveness_score === "number") ? Math.round(l.liveness_score * 100) : null;
        const ts = l.created_at ? new Date(l.created_at).toLocaleString() : "—";
        return (
          <div key={l.kyc_id || i} className="border-b border-[var(--zc-border)] last:border-0">
            <button
              onClick={() => setExpanded(s => ({ ...s, [l.kyc_id]: !isOpen }))}
              className="w-full flex items-start gap-3 px-4 sm:px-6 py-3 text-left hover:bg-[var(--zc-bg-2)]"
              data-testid={`kyc-log-row-${l.kyc_id}`}
            >
              <span className="shrink-0 mt-0.5">{isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
              <div className="font-mono zc-meta truncate hidden sm:block sm:w-[140px] shrink-0">{l.kyc_id}</div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate">{l.user_name || l.user_id} <span className="text-[var(--zc-text-3)] text-xs">· {l.user_email || ""}</span></div>
                <div className="zc-hint mt-0.5">
                  <span className="font-mono sm:hidden">{l.kyc_id} · </span>
                  {l.provider} · {ts}
                  {l.id_status && <> · ID <b>{l.id_status}</b></>}
                  {score !== null && <> · liveness <b>{score}%</b></>}
                </div>
              </div>
              <div className="zc-badge shrink-0" style={{ background: "var(--zc-bg-2)", color: colorFor(l.status) }}>
                {l.status}
              </div>
            </button>
            {isOpen && (
              <div className="px-4 sm:px-12 pb-4 grid sm:grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="eyebrow mb-1">Decision context</div>
                  <div className="space-y-0.5 text-[var(--zc-text-2)]">
                    <div><b>Decline:</b> {l.decline_reason || "—"}</div>
                    <div><b>Review:</b> {l.review_reason || "—"}</div>
                    <div><b>ID parsed:</b> {l.id_first_name || "—"} {l.id_last_name || ""} {l.id_document_type ? `· ${l.id_document_type}` : ""}</div>
                    <div><b>DOB:</b> {l.id_dob || "—"} · <b>Doc#:</b> {l.id_document_number || "—"}</div>
                    {Array.isArray(l.id_warnings) && l.id_warnings.length > 0 && (
                      <div><b>ID warnings:</b> {l.id_warnings.join(", ")}</div>
                    )}
                    <div><b>Didit request_ids:</b> <span className="font-mono break-all">{JSON.stringify(l.didit_request_ids || {})}</span></div>
                  </div>
                </div>
                <div>
                  <div className="eyebrow mb-1">Raw Didit payload</div>
                  <pre className="text-2xs font-mono bg-[var(--zc-bg-2)] p-2 rounded max-h-64 overflow-auto whitespace-pre-wrap break-all border border-[var(--zc-border)]" data-testid={`kyc-log-raw-${l.kyc_id}`}>
                    {JSON.stringify(l.didit_raw || {}, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
